export const normalizeActivityId = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	const parsedValue = Number(value);
	return Number.isNaN(parsedValue) ? value : parsedValue;
};

export const ACTIVITY_STATUS_LABELS = Object.freeze({
	1: 'Arama Bekliyor',
	2: 'Arandı, fırsat oluşturuldu',
	3: 'Sohbet Bekliyor',
	4: 'Sohbet Tamamlandı',
	5: 'Sohbet Devam Ediyor',
	6: 'Tekrar Aranacak',
	7: 'Arandı, ulaşılamadı',
	8: 'Arandı, satış dışı',
	9: 'Arandı ürün hizmet bilgisi verildi',
	10: 'Aranmayacak, satış dışı',
	11: 'Aranmayacak, niteliksiz',
	12: 'Yetkili ile görüşülemedi',
	13: 'Uygunsuz içerik'
});

const CANONICAL_ACTIVITY_LABELS = Object.values(ACTIVITY_STATUS_LABELS);

const normalizeActivityLabel = (value) => String(value || '')
	.trim()
	.toLocaleLowerCase('tr-TR')
	.normalize('NFD')
	.replace(/[\u0300-\u036f]/g, '')
	.replace(/ı/g, 'i')
	.replace(/[^a-z0-9]/g, '');

const getCanonicalActivityLabel = (label) => {
	const normalizedLabel = normalizeActivityLabel(label);
	return CANONICAL_ACTIVITY_LABELS.find(
		canonicalLabel => normalizeActivityLabel(canonicalLabel) === normalizedLabel
	);
};

export const getActivityStatusLabel = (activityId, fallbackLabel = '') => {
	const normalizedActivityId = normalizeActivityId(activityId);
	return ACTIVITY_STATUS_LABELS[normalizedActivityId]
		|| getCanonicalActivityLabel(fallbackLabel)
		|| fallbackLabel;
};

export const getActivityStatusValue = (activityId, activityName = '') => {
	const normalizedActivityId = normalizeActivityId(activityId);
	if (ACTIVITY_STATUS_LABELS[normalizedActivityId]) {
		return normalizedActivityId;
	}

	const canonicalActivityLabel = getCanonicalActivityLabel(activityName);
	if (canonicalActivityLabel) {
		const matchingEntry = Object.entries(ACTIVITY_STATUS_LABELS)
			.find(([, label]) => label === canonicalActivityLabel);
		return matchingEntry ? Number(matchingEntry[0]) : undefined;
	}

	return undefined;
};

/**
 * Resolves the activity shown in conversation detail. Some passive-conversation
 * payloads return activityId=0 even though the chat-list item has the customer's
 * current activity. In that case the list value is authoritative.
 */
export const resolveConversationActivity = ({
	detailActivityId,
	detailActivityName = '',
	listActivityId,
	listActivityName = ''
}) => {
	const normalizedDetailActivityId = normalizeActivityId(detailActivityId);
	const detailValue = getActivityStatusValue(detailActivityId, detailActivityName);
	const listValue = getActivityStatusValue(listActivityId, listActivityName);
	const detailIsLegacyZero = normalizedDetailActivityId === 0;
	const shouldPreferList = detailIsLegacyZero || detailValue === undefined;

	// A zero detail id is a backend placeholder, and its accompanying name can
	// also be the default "Arama Bekliyor" text. Do not treat either as the
	// customer's actual status when the matching list row has no usable value.
	const activityId = detailIsLegacyZero
		? listValue
		: (shouldPreferList ? (listValue ?? detailValue) : (detailValue ?? listValue));
	const activityName = activityId === listValue && shouldPreferList
		? listActivityName
		: detailActivityName;

	return {
		activityId,
		activityName: activityId === undefined
			? ''
			: getActivityStatusLabel(activityId, activityName)
	};
};

export const getActivityStatusOptions = () => Object.entries(ACTIVITY_STATUS_LABELS)
	.map(([value, label]) => ({ value: Number(value), label }));

export const hasVisibleActivity = (activityId, activityName) => {
	if (normalizeActivityId(activityId) === null) {
		return false;
	}

	if (typeof activityName === 'string') {
		return activityName.trim().length > 0;
	}

	return Boolean(activityName);
};

export const isActivityMatch = (leftActivityId, rightActivityId) => {
	return normalizeActivityId(leftActivityId) === normalizeActivityId(rightActivityId);
};
